"use client";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { FilterDrawer } from "@/components/Common/FilterDrawer";
import { TrainingCard } from "../Common/TrainingCard";
import FilterIcon from "../SVG/FilterIcon";
import SearchIcon from "../SVG/SearchIcon";
import CatalogFilterSidebar from "@/components/Common/CatalogFilterSidebar";
import CategorySelector, {
  type CategoryItem,
} from "@/components/Common/CategorySelector";
import { useGetCategories } from "@/actions/category";
import { useGetFormations } from "@/actions/formation";
import { toBookmark } from "@/lib/bookmarks";
import {
  DEFAULT_PRICE_RANGE,
  PRICE_MAX,
  PRICE_MIN,
  useCatalogFilters,
  type CatalogFilterInit,
} from "@/hooks/useCatalogFilters";
import SearchBar, { type SearchBarFilters } from "../Common/SearchBar";

// ── Static category meta (icons only — no longer drives card data) ────────────

type StaticKey =
  | "marketingCards"
  | "businessCards"
  | "socialCards"
  | "toolsCards"
  | "leadershipCards"
  | "all";

const STATIC_CATEGORIES: Array<{
  key: StaticKey;
  label: string;
  icon: string;
}> = [
  {
    key: "marketingCards",
    label: "Marketing et Digital",
    icon: "/assets/icons/marketing-digital-phone.png",
  },
  {
    key: "businessCards",
    label: "Développement Commercial et Entreprenariat",
    icon: "/assets/icons/launching.svg",
  },
  {
    key: "socialCards",
    label: "Réseaux Sociaux et Contenus",
    icon: "/assets/icons/social-networks-and-content.svg",
  },
  {
    key: "toolsCards",
    label: "Outils et Productivité",
    icon: "/assets/icons/tools-and-productivity.svg",
  },
  {
    key: "leadershipCards",
    label: "Développement Personnel et Leadership",
    icon: "/assets/icons/personal-development-and-leadership.svg",
  },
  { key: "all", label: "Sélectionner...", icon: "/assets/icons/sitemap.svg" },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  /** Category slug from the URL segment, e.g. "marketing-digital". "all" or
   *  undefined means no category filter (show everything). */
  initialCategory?: string;
  /** Filters carried over from the home-page search (via URL query params),
   *  used to seed the catalog filters on first render. */
  initialSearch?: CatalogFilterInit;
}

export default function FormationCatalog({
  initialCategory = "all",
  initialSearch,
}: Props) {
  const router = useRouter();
  const { categories } = useGetCategories();

  // ── Category state — seeded from URL, kept in sync when prop changes ──────
  const [selectedCategory, setSelectedCategory] =
    useState<string>(initialCategory);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedCategory(initialCategory ?? "all");
  }, [initialCategory]);

  // ── API: formations filtered by selected category ─────────────────────────
  const { formations, formationsLoading } = useGetFormations(
    selectedCategory !== "all" ? selectedCategory : undefined,
  );

  // ── Build flat selector list from live API data ───────────────────────────
  const selectorItems = useMemo<CategoryItem[]>(() => {
    if (!categories.length) {
      return STATIC_CATEGORIES.map((c) => ({ key: c.key, label: c.label }));
    }

    const items: CategoryItem[] = [
      { key: "all", label: "Toutes nos formations en un clin d'œil" },
    ];

    for (const cat of categories) {
      items.push({ key: cat.slug, label: cat.name });
      for (const sub of cat.subcategories ?? []) {
        items.push({ key: sub.slug, label: sub.name, isSubcategory: true });
      }
    }

    return items;
  }, [categories]);

  // ── Filter / sort state + client-side filtering (shared hook) ─────────────
  const filters = useCatalogFilters(formations, initialSearch);
  const {
    visibleCards,
    query,
    setQuery,
    setSortOrder,
    selectedMode,
    setSelectedMode,
    selectedVille,
    setSelectedVille,
    selectedFinancing,
    priceRange,
    setPriceRange,
    mobileFilterOpen,
    setMobileFilterOpen,
    mobileSearchOpen,
    setMobileSearchOpen,
    handleResetFilters,
    handleToggleFinancing,
    handleRemoveFinancing,
    handleClearPrice,
  } = filters;

  const [isCategoriesOpen, setIsCategoriesOpen] = useState(true);

  // ── Derived selection meta ────────────────────────────────────────────────
  const selectedMeta = useMemo(
    () =>
      selectorItems.find((c) => c.key === selectedCategory) ?? selectorItems[0],
    [selectedCategory, selectorItems],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  /** Update local state AND push new URL — no full reload. */
  const handleCategoryChange = (slug: string) => {
    setSelectedCategory(slug);
    const target = slug === "all" ? "/formations" : `/formations/${slug}`;
    router.push(target, { scroll: false });
  };

  /** SearchBar owns keywords/category/ville/presentiel/distance — map them onto
   *  the same state already driving the sidebar filters, so there's a single
   *  source of truth for "what's currently filtered". */
  const handleSearchBarFilters = (searchFilters: SearchBarFilters) => {
    setQuery(searchFilters.keywords);
    if (searchFilters.category !== selectedCategory) {
      handleCategoryChange(searchFilters.category);
    }
    setSelectedVille(searchFilters.ville);
    setSelectedMode(
      searchFilters.presentiel && !searchFilters.distance
        ? "Présentiel"
        : searchFilters.distance && !searchFilters.presentiel
          ? "Distanciel"
          : null,
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="md:px-7 px-4 pb-13.75 md:pb-20 mt-8 md:mt-10.75">
      <div className="max-w-346.25 mx-auto">
        <div className="flex md:flex-row flex-col md:gap-0 gap-11">
          {/* ── Sidebar ────────────────────────────────────────────────── */}
          <aside className="md:pr-6 mt-2.5">
            <div className="min-w-70 lg:min-w-81">
              <div className="flex items-center justify-between mb-3 md:mb-5">
                <h3 className="text-[#1B365E] font-light text-[18px] leading-5 md:text-[30px] md:leading-13.25">
                  Nos catégories :
                </h3>
                <button
                  type="button"
                  aria-label={isCategoriesOpen ? "reduce" : "expand"}
                  onClick={() => setIsCategoriesOpen((v) => !v)}
                  className="cursor-pointer w-4 h-4"
                >
                  <Image
                    className="w-4 shrink-0 md:hidden block"
                    src={
                      isCategoriesOpen
                        ? "/assets/icons/minus.svg"
                        : "/assets/icons/plus.svg"
                    }
                    alt={isCategoriesOpen ? "minus" : "plus"}
                    width={16}
                    height={16}
                  />
                </button>
              </div>

              {isCategoriesOpen && (
                <div className="mb-[17.25px]">
                  <CategorySelector
                    categories={selectorItems}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={handleCategoryChange}
                  />
                </div>
              )}

              {/* Mobile: "All" button + search/filter icons */}
              <div className="md:hidden flex items-center justify-between flex-wrap gap-6">
                <button
                  onClick={() => handleCategoryChange("all")}
                  className={`cursor-pointer group w-fit flex items-center gap-2 px-4 md:px-6 py-2 md:py-4 text-[8px] md:text-xs font-bold text-left border border-[#EFEFEF] rounded-full transition-colors ${
                    selectedCategory === "all"
                      ? "bg-[#1B365E] text-white"
                      : "bg-white text-[#1B365E] hover:bg-[#F5FBFD]"
                  }`}
                >
                  <svg
                    width="25"
                    height="22"
                    viewBox="0 0 25 22"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`transition-colors md:w-6.25 w-[13.64px] md:h-5.5 h-3 ${selectedCategory === "all" ? "text-white" : "text-[#1B365E]"}`}
                  >
                    <path
                      d="M15.9722 3.4375C15.9722 2.29883 15.0391 1.375 13.8889 1.375H11.1111C9.96094 1.375 9.02778 2.29883 9.02778 3.4375V6.1875C9.02778 7.32617 9.96094 8.25 11.1111 8.25H11.4583V9.96875H4.86111C3.51997 9.96875 2.43056 11.0473 2.43056 12.375V13.75H2.08333C0.933161 13.75 0 14.6738 0 15.8125V18.5625C0 19.7012 0.933161 20.625 2.08333 20.625H4.86111C6.01129 20.625 6.94444 19.7012 6.94444 18.5625V15.8125C6.94444 14.6738 6.01129 13.75 4.86111 13.75H4.51389V12.375C4.51389 12.1859 4.67014 12.0312 4.86111 12.0312H11.4583V13.75H11.1111C9.96094 13.75 9.02778 14.6738 9.02778 15.8125V18.5625C9.02778 19.7012 9.96094 20.625 11.1111 20.625H13.8889C15.0391 20.625 15.9722 19.7012 15.9722 18.5625V15.8125C15.9722 14.6738 15.0391 13.75 13.8889 13.75H13.5417V12.0312H20.1389C20.3299 12.0312 20.4861 12.1859 20.4861 12.375V13.75H20.1389C18.9887 13.75 18.0556 14.6738 18.0556 15.8125V18.5625C18.0556 19.7012 18.9887 20.625 20.1389 20.625H22.9167C24.0668 20.625 25 19.7012 25 18.5625V15.8125C25 14.6738 24.0668 13.75 22.9167 13.75H22.5694V12.375C22.5694 11.0473 21.48 9.96875 20.1389 9.96875H13.5417V8.25H13.8889C15.0391 8.25 15.9722 7.32617 15.9722 6.1875V3.4375Z"
                      fill="currentColor"
                    />
                  </svg>
                  {selectorItems.find((c) => c.key === "all")?.label}
                </button>
                <div className="flex items-center gap-6">
                  <button
                    aria-label="Afficher la recherche"
                    aria-expanded={mobileSearchOpen}
                    onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
                    className="shrink-0 w-6 h-6 rounded-full"
                  >
                    <SearchIcon
                      className="w-6 h-6"
                      circleColor="#1B365E"
                      strokeColor="#ffffff"
                    />
                  </button>
                  <button
                    aria-label="filter"
                    onClick={() => setMobileFilterOpen(true)}
                    className="shrink-0 w-6 h-6 rounded-full"
                  >
                    <FilterIcon
                      className="w-6 h-6"
                      circleColor="#1B365E"
                      strokeColor="#ffffff"
                    />
                  </button>
                </div>
              </div>

              {/* Desktop filters */}
              <CatalogFilterSidebar controller={filters} />
            </div>
          </aside>  

          {/* ── Main content ────────────────────────────────────────────── */}
          <div className="w-full md:mt-5">
            {/* <h3 className="md:pl-8 md:block hidden text-[#1B365E] font-light text-[30px] leading-10 mb-6.5">
              Cherchez une formation :
            </h3> */}
            <div className="md:pl-8 md:border-l border-[#40BFDE] md:min-h-275.75">
              <div className={`${mobileSearchOpen ? "block" : "hidden"} md:block`}>
                <SearchBar
                  initialFilters={{
                    keywords: query,
                    category: selectedCategory,
                    ville: selectedVille,
                    presentiel: selectedMode === "Présentiel",
                    distance: selectedMode === "Distanciel",
                  }}
                  onFilterChange={handleSearchBarFilters}
                />
              </div>

              {/* Header: icon + title */}
              <div className="flex items-center md:justify-between justify-center flex-wrap xl:flex-nowrap flex-1 gap-3 mb-3 md:mb-7.5">
                {/* <div className="flex md:flex-row flex-col items-center md:text-left text-center md:justify-start justify-center md:gap-5 -gap-1">
                  <Image
                    src={selectedIcon.src}
                    alt={selectedMeta?.label ?? "catégorie"}
                    width={selectedIcon.width}
                    height={selectedIcon.height}
                  />
                  <h2 className="text-[#1B365E] font-light font-red-hat tracking-[0.2px] text-[30px] leading-10 lg:text-[50px] lg:leading-19">
                    {selectedMeta?.label ?? ""}
                  </h2>
                </div> */}
                <button
                  className="cursor-pointer hidden md:inline-flex items-center justify-center shrink-0 w-10 mt-8"
                  onClick={() =>
                    setSortOrder((prev) =>
                      prev === "asc" ? "desc" : prev === "desc" ? null : "asc",
                    )
                  }
                  aria-label="Trier par prix"
                >
                  <Image
                    src="/assets/icons/sort.svg"
                    alt="sort-icon"
                    width={40}
                    height={32}
                  />
                </button>
              </div>

              {/* Cards grid */}
              {formationsLoading ? (
                <div className="catalog-grid items-start gap-8 xl:gap-19">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-full h-64 bg-gray-100 animate-pulse rounded-2xl"
                    />
                  ))}
                </div>
              ) : visibleCards.length === 0 ? (
                <p className="text-[#676767] text-sm mt-8">
                  Aucune formation trouvée.
                </p>
              ) : (
                <div className="catalog-grid auto-rows-fr gap-8 xl:gap-19">
                  {visibleCards.map((f, index) => (
                    <div
                      key={f.id}
                      className={`w-full h-full justify-self-center ${index % 2 === 0 ? "lg:justify-self-start" : "lg:justify-self-end"} ${index % 3 === 0 ? "xl:justify-self-start" : index % 3 === 1 ? "xl:justify-self-center" : "xl:justify-self-end"}`}
                    >
                      <TrainingCard
                        title={f.title}
                        description={f.description}
                        category={f.category}
                        location={f.location}
                        duration={f.duration}
                        price={f.price}
                        financing={f.financing ?? ""}
                        isNew={f.isNew}
                        customClass="w-full min-h-full xl:w-71"
                        formation={toBookmark(f)}
                        onDiscover={() =>
                          router.push(`/formation/${f.slug}`)
                        }
                        onSubscribe={() =>
                          router.push(`/formation/${f.slug}`)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawers */}
      <FilterDrawer
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        controller={filters}
        categoryLabel={selectedMeta?.label ?? ""}
        priceMin={PRICE_MIN}
        priceMax={PRICE_MAX}
        priceRange={priceRange}
        defaultPriceRange={DEFAULT_PRICE_RANGE}
        onPriceRangeChange={setPriceRange}
        onClearPrice={handleClearPrice}
        selectedMode={selectedMode}
        onModeChange={setSelectedMode}
        selectedFinancing={selectedFinancing}
        onToggleFinancing={handleToggleFinancing}
        onRemoveFinancing={handleRemoveFinancing}
        onReset={handleResetFilters}
        onApply={() => setMobileFilterOpen(false)}
      />
    </section>
  );
}
